import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, resolveDirective as _resolveDirective, createVNode as _createVNode, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "candidate-debug" }
const _hoisted_2 = { class: "candidate-debug__header" }
const _hoisted_3 = { class: "candidate-debug__meta" }
const _hoisted_4 = { class: "candidate-debug__summary" }
const _hoisted_5 = { class: "candidate-debug__success" }
const _hoisted_6 = { class: "candidate-debug__warning" }
const _hoisted_7 = { class: "candidate-debug__toolbar" }
const _hoisted_8 = { class: "candidate-debug__search" }
const _hoisted_9 = {
  key: 0,
  class: "candidate-debug__error"
}
const _hoisted_10 = { class: "candidate-debug__subtle" }
const _hoisted_11 = { class: "candidate-debug__subtle" }
const _hoisted_12 = { class: "candidate-debug__subtle" }
const _hoisted_13 = {
  key: 0,
  class: "candidate-debug__subtle"
}
const _hoisted_14 = { class: "candidate-debug__subtle" }
const _hoisted_15 = { class: "candidate-debug__subtle" }
const _hoisted_16 = {
  key: 0,
  class: "candidate-debug__subtle"
}
const _hoisted_17 = { class: "candidate-debug__subtle" }
const _hoisted_18 = { class: "candidate-debug__tags" }

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import Button from 'primevue/button'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import InputText from 'primevue/inputtext'
import SelectButton from 'primevue/selectbutton'
import Tag from 'primevue/tag'
import { api } from '@/api/http'
import { formatPrice } from '@/common/function'
import {
  ArtefactFilterSettings,
  DEFAULT_ARTEFACT_FILTER_SETTINGS,
  LiquidityWindow,
  getArtefactFilterReasons,
  passesArtefactFilters
} from '@/common/artefact-filters'

type CandidateMode = 'active' | 'hidden' | 'disappeared'
type DisappearanceReason =
  | 'no_active_lot'
  | 'no_longer_profitable'
  | 'cheapest_lot_changed'
  | 'group_missing'

interface CandidateRecord {
  id: string
  artefactId: string
  name: string
  qlt: number
  ptn: string
  firstSeen: string
  lastSeen: string
  disappearedAt: string | null
  status: 'active' | 'disappeared'
  disappearanceReason: DisappearanceReason | null
  observations: number
  currentPrice: number
  salePrice: number
  calculatedSalePrice: number
  averagePrice: number
  rawProfit: number
  minRequiredProfit: number
  roiPercent: number
  historyCount: number
  sales24h: number
  sales48h: number
}

interface CandidateState {
  enabled: boolean
  updatedAt: string | null
  lastScanCompletedAt: string | null
  lastScanDurationSeconds: number | null
  configuredArtefacts: number
  scan: {
    inProgress: boolean
    startedAt: string | null
    processedArtefacts: number
    totalArtefacts: number
  }
  history: { pageSize: number; maxRecords: number; order: string }
  summary: { active: number; visible: number; hidden: number; disappeared: number }
  records: CandidateRecord[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ArtefactCandidateDebug',
  props: {
    onlyGoodLots: { type: Boolean, default: DEFAULT_ARTEFACT_FILTER_SETTINGS.onlyLiquid },
    liquidityWindow: { default: DEFAULT_ARTEFACT_FILTER_SETTINGS.liquidityWindow },
    minLiquiditySales: { default: DEFAULT_ARTEFACT_FILTER_SETTINGS.minSales },
    minBuyoutProfit: { default: DEFAULT_ARTEFACT_FILTER_SETTINGS.minProfit }
  },
  setup(__props: any) {

const props = __props

const modeOptions = [
  { label: 'Активные', value: 'active' },
  { label: 'Скрытые', value: 'hidden' },
  { label: 'Исчезнувшие', value: 'disappeared' }
]
const disappearanceLabels: Record<DisappearanceReason, string> = {
  no_active_lot: 'Лот снят или выкуплен',
  no_longer_profitable: 'Спред исчез',
  cheapest_lot_changed: 'Сменился дешевый лот',
  group_missing: 'Группа исчезла'
}
const state = ref<CandidateState | null>(null)
const mode = ref<CandidateMode>('active')
const search = ref('')
const loading = ref(false)
const error = ref('')
const now = ref(Date.now())
let timer: number | null = null
let secondsUntilRefresh = 0

const filterSettings = computed<ArtefactFilterSettings>(() => ({
  onlyLiquid: props.onlyGoodLots,
  liquidityWindow: props.liquidityWindow,
  minSales: props.minLiquiditySales,
  minProfit: props.minBuyoutProfit
}))
const activeRecords = computed(() =>
  (state.value?.records ?? []).filter(record => record.status === 'active')
)
const activeCount = computed(() => activeRecords.value.length)
const visibleCount = computed(
  () =>
    activeRecords.value.filter(record =>
      passesArtefactFilters(record, filterSettings.value, now.value)
    ).length
)
const hiddenCount = computed(() => activeCount.value - visibleCount.value)
const disappearedCount = computed(
  () => (state.value?.records ?? []).filter(record => record.status === 'disappeared').length
)

const filteredRecords = computed(() => {
  const query = search.value.toLocaleLowerCase('ru-RU')

  return (state.value?.records ?? [])
    .filter(record => {
      if (mode.value === 'active' && record.status !== 'active') return false
      if (
        mode.value === 'hidden' &&
        (record.status !== 'active' ||
          passesArtefactFilters(record, filterSettings.value, now.value))
      ) {
        return false
      }
      if (mode.value === 'disappeared' && record.status !== 'disappeared') return false

      return (
        !query || `${record.name} ${record.artefactId}`.toLocaleLowerCase('ru-RU').includes(query)
      )
    })
    .sort((a, b) => {
      if (a.status === 'active' && b.status === 'active') return b.rawProfit - a.rawProfit
      return (
        new Date(b.disappearedAt ?? b.lastSeen).getTime() -
        new Date(a.disappearedAt ?? a.lastSeen).getTime()
      )
    })
})

const lastScanLabel = computed(() => {
  if (state.value?.scan.inProgress) {
    const elapsed = state.value.scan.startedAt
      ? Math.max(0, now.value - new Date(state.value.scan.startedAt).getTime())
      : 0

    return `${state.value.scan.processedArtefacts}/${state.value.scan.totalArtefacts} · ${formatAgo(
      elapsed
    )}`
  }

  if (!state.value?.lastScanCompletedAt) return 'нет данных'

  const ago = Math.max(0, now.value - new Date(state.value.lastScanCompletedAt).getTime())
  return `${formatAgo(ago)} назад · ${state.value.lastScanDurationSeconds ?? 0}с`
})

async function loadCandidates() {
  if (loading.value) return

  loading.value = true
  error.value = ''

  try {
    const response = await api.get<CandidateState>('/artefact/debug/candidates')
    state.value = response.data
    secondsUntilRefresh = 10
  } catch {
    error.value = 'Не удалось загрузить журнал кандидатов'
  } finally {
    loading.value = false
  }
}

function statusLabel(record: CandidateRecord) {
  if (record.status === 'disappeared') return 'Исчез'
  return passesArtefactFilters(record, filterSettings.value, now.value) ? 'В таблице' : 'Скрыт'
}

function statusSeverity(record: CandidateRecord) {
  if (record.status === 'disappeared') return 'secondary'
  return passesArtefactFilters(record, filterSettings.value, now.value) ? 'success' : 'warn'
}

function filterReasonLabels(record: CandidateRecord): string[] {
  return getArtefactFilterReasons(record, filterSettings.value, now.value).map(reason => {
    if (reason === 'below_internal_profit') return 'Ниже внутреннего порога'
    if (reason === 'insufficient_history') return 'Мало истории'
    if (reason === 'below_configured_profit') {
      return `Профит < ${formatPrice(props.minBuyoutProfit)}`
    }
    if (reason === 'insufficient_liquidity') {
      return `Продаж < ${props.minLiquiditySales} за ${props.liquidityWindow}ч`
    }
    if (reason === 'no_recent_sale') return 'Нет продаж за 24ч'
    return 'Продажа не ниже медианы'
  })
}

function valueClass(value: number) {
  return value >= 0 ? 'candidate-debug__success' : 'candidate-debug__danger'
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(new Date(value))
}

function formatDuration(firstSeen: string, disappearedAt: string | null) {
  const end = disappearedAt ? new Date(disappearedAt).getTime() : now.value
  return formatAgo(Math.max(0, end - new Date(firstSeen).getTime()))
}

function formatAgo(milliseconds: number) {
  const totalSeconds = Math.floor(milliseconds / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours) return `${hours}ч ${minutes}м`
  if (minutes) return `${minutes}м ${seconds}с`
  return `${seconds}с`
}

onMounted(() => {
  void loadCandidates()
  timer = window.setInterval(() => {
    now.value = Date.now()
    secondsUntilRefresh -= 1
    if (secondsUntilRefresh <= 0) void loadCandidates()
  }, 1000)
})

onBeforeUnmount(() => {
  if (timer !== null) window.clearInterval(timer)
})

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createElementVNode("div", null, [
        _cache[2] || (_cache[2] = _createElementVNode("h2", null, "Диагностика кандидатов", -1)),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("span", null, "Состояние обхода: " + _toDisplayString(lastScanLabel.value), 1),
          _createElementVNode("span", null, _toDisplayString(state.value?.configuredArtefacts ?? 0) + " артефактов", 1),
          _createElementVNode("span", null, "История: " + _toDisplayString(state.value?.history.maxRecords ?? 1200) + " последних продаж", 1)
        ])
      ]),
      _withDirectives(_createVNode(_unref(Button), {
        icon: "pi pi-refresh",
        text: "",
        rounded: "",
        "aria-label": "Обновить журнал",
        loading: loading.value,
        onClick: loadCandidates
      }, null, 8, ["loading"]), [
        [
          _directive_tooltip,
          'Обновить журнал',
          void 0,
          { bottom: true }
        ]
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", null, [
        _cache[3] || (_cache[3] = _createElementVNode("span", null, "Активные", -1)),
        _createElementVNode("strong", null, _toDisplayString(activeCount.value), 1)
      ]),
      _createElementVNode("div", null, [
        _cache[4] || (_cache[4] = _createElementVNode("span", null, "В основной таблице", -1)),
        _createElementVNode("strong", _hoisted_5, _toDisplayString(visibleCount.value), 1)
      ]),
      _createElementVNode("div", null, [
        _cache[5] || (_cache[5] = _createElementVNode("span", null, "Скрыты фильтрами", -1)),
        _createElementVNode("strong", _hoisted_6, _toDisplayString(hiddenCount.value), 1)
      ]),
      _createElementVNode("div", null, [
        _cache[6] || (_cache[6] = _createElementVNode("span", null, "Исчезли", -1)),
        _createElementVNode("strong", null, _toDisplayString(disappearedCount.value), 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_7, [
      _createVNode(_unref(SelectButton), {
        modelValue: mode.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((mode).value = $event)),
        options: modeOptions,
        optionLabel: "label",
        optionValue: "value",
        allowEmpty: false
      }, null, 8, ["modelValue"]),
      _createElementVNode("span", _hoisted_8, [
        _cache[7] || (_cache[7] = _createElementVNode("i", {
          class: "pi pi-search",
          "aria-hidden": "true"
        }, null, -1)),
        _createVNode(_unref(InputText), {
          modelValue: search.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((search).value = $event)),
          placeholder: "Артефакт или ID"
        }, null, 8, ["modelValue"])
      ])
    ]),
    (error.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(error.value), 1))
      : _createCommentVNode("", true),
    _createVNode(_unref(DataTable), {
      value: filteredRecords.value,
      dataKey: "id",
      paginator: "",
      rows: 20,
      rowsPerPageOptions: [20, 50, 100],
      stripedRows: "",
      responsiveLayout: "scroll",
      class: "candidate-debug__table"
    }, {
      empty: _withCtx(() => _cache[8] || (_cache[8] = [
        _createTextVNode("Кандидатов в этом разделе пока нет", -1)
      ])),
      default: _withCtx(() => [
        _createVNode(_unref(Column), {
          header: "Статус",
          style: {"width":"8.5rem"}
        }, {
          body: _withCtx(({ data }) => [
            _createVNode(_unref(Tag), {
              value: statusLabel(data),
              severity: statusSeverity(data)
            }, null, 8, ["value", "severity"])
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "name",
          header: "Артефакт",
          sortable: "",
          style: {"min-width":"10rem"}
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("strong", null, _toDisplayString(data.name), 1),
            _createElementVNode("div", _hoisted_10, _toDisplayString(data.artefactId) + " · q" + _toDisplayString(data.qlt) + " · +" + _toDisplayString(data.ptn), 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "rawProfit",
          header: "Профит",
          sortable: "",
          style: {"min-width":"8rem"}
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("strong", {
              class: _normalizeClass(valueClass(data.rawProfit))
            }, _toDisplayString(_unref(formatPrice)(data.rawProfit)), 3),
            _createElementVNode("div", _hoisted_11, _toDisplayString(data.roiPercent.toFixed(1)) + "%", 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          header: "Цены",
          style: {"min-width":"11rem"}
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", null, _toDisplayString(_unref(formatPrice)(data.currentPrice)) + " → " + _toDisplayString(_unref(formatPrice)(data.salePrice)), 1),
            _createElementVNode("div", _hoisted_12, "медиана " + _toDisplayString(_unref(formatPrice)(data.averagePrice)), 1),
            (data.calculatedSalePrice !== data.salePrice)
              ? (_openBlock(), _createElementBlock("div", _hoisted_13, " расчет " + _toDisplayString(_unref(formatPrice)(data.calculatedSalePrice)), 1))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "sales24h",
          header: "Продажи",
          sortable: "",
          style: {"min-width":"7.5rem"}
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", null, _toDisplayString(data.sales24h) + " / 24ч", 1),
            _createElementVNode("div", _hoisted_14, _toDisplayString(data.sales48h) + " / 48ч · " + _toDisplayString(data.historyCount) + " всего ", 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          header: "Жизненный цикл",
          style: {"min-width":"11rem"}
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", null, _toDisplayString(formatDuration(data.firstSeen, data.disappearedAt)), 1),
            _createElementVNode("div", _hoisted_15, "с " + _toDisplayString(formatDate(data.firstSeen)), 1),
            (data.disappearedAt)
              ? (_openBlock(), _createElementBlock("div", _hoisted_16, " до " + _toDisplayString(formatDate(data.disappearedAt)), 1))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_17, _toDisplayString(data.observations) + " проверок", 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          header: "Причины",
          style: {"min-width":"15rem"}
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", _hoisted_18, [
              (data.disappearanceReason)
                ? (_openBlock(), _createBlock(_unref(Tag), {
                    key: 0,
                    value: disappearanceLabels[data.disappearanceReason],
                    severity: "secondary"
                  }, null, 8, ["value"]))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filterReasonLabels(data), (reason) => {
                return (_openBlock(), _createBlock(_unref(Tag), {
                  key: reason,
                  value: reason,
                  severity: "warn"
                }, null, 8, ["value"]))
              }), 128)),
              (!data.disappearanceReason && !filterReasonLabels(data).length)
                ? (_openBlock(), _createBlock(_unref(Tag), {
                    key: 1,
                    value: "Все фильтры пройдены",
                    severity: "success"
                  }))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["value"])
  ]))
}
}

})